import React, { useState } from "react";
import { Label, Text, Dropdown, Option, makeStyles } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import SearchResourceInGraph from "../../GenericComponent/SearchResourceInGraph";
import QueueSelectorComponent from "../../GenericComponent/QueueSelectorComponent";
import "./CSS/TimeComponent.css";

const useStyles = makeStyles({
  row: { display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1rem", marginTop: "1rem" },
  timeRow: { display: "flex", gap: "1rem", alignItems: "center" },
  section: { marginTop: "1rem", display: "flex", flexDirection: "column", gap: "0.5rem" },
  input: { padding: "4px 8px", borderRadius: "4px", border: "none", width: "120px", backgroundColor: "#f3f2f1" },
  label: { fontWeight: 600, minWidth: "150px" },
});

export default function TimeComponent({ data, routingName, onChange, setOnMatched, setOnUnMatched, routingList, permissions, isEditMode }) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [startError, setStartError] = useState("");
  const [endError, setEndError] = useState("");

  const [matchedAction, setMatchedAction] = useState(data.onMatched?.type || "");
  const [unmatchedAction, setUnmatchedAction] = useState(data.onUnMatched?.type || "");

  const [selectedMatchedQueue, setSelectedMatchedQueue] = useState({
    displayName: data.onMatched?.queueDisplayName || "",
    id: data.onMatched?.queueId || "",
  });

  const [selectedMatchedUser, setSelectedMatchedUser] = useState({
    displayName: data.onMatched?.userDisplayName || "",
    id: data.onMatched?.targetId || "",
  });

  const [selectedMatchedRouting, setSelectedMatchedRouting] = useState(data.onMatched?.routingName || "");
  const [matchedPhone, setMatchedPhone] = useState(
    data.onMatched?.type === "transfer" && data.onMatched?.targetType === "phone" ? data.onMatched?.targetId || "" : ""
  );

  const [selectedUnmatchedQueue, setSelectedUnmatchedQueue] = useState({
    displayName: data.onUnMatched?.queueDisplayName || "",
    id: data.onUnMatched?.queueId || "",
  });

  const [selectedUnmatchedUser, setSelectedUnmatchedUser] = useState({
    displayName: data.onUnMatched?.userDisplayName || "",
    id: data.onUnMatched?.targetId || "",
  });

  const [selectedUnmatchedRouting, setSelectedUnmatchedRouting] = useState(data.onUnMatched?.routingName || "");
  const [unmatchedPhone, setUnmatchedPhone] = useState(
    data.onUnMatched?.type === "transfer" && data.onUnMatched?.targetType === "phone" ? data.onUnMatched?.targetId || "" : ""
  );

  const REGEX_NEXT_ACTIONS = [
    { value: "queue", textKey: "queue" },
    { value: "transferUser", textKey: "transferToUser" },
    { value: "disconnect", textKey: "disconnect" },
    { value: "routing", textKey: "routing" },
    { value: "transferPhone", textKey: "transferToPhone" },
  ];

  const getTranslatedValue = (type, targetType) => {
    let value = type;
    if (type === "transfer") {
      value = targetType === "phone" ? "transferPhone" : "transferUser";
    }
    return t(REGEX_NEXT_ACTIONS.find(a => a.value === value)?.textKey || value);
  };

  const handleTimeChange = (value, type) => {
    const isValid = /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
    if (isValid) {
      type === "start" ? setStartError("") : setEndError("");
      onChange(routingName, `Time.${type}`, value);
    } else {
      type === "start" ? setStartError(t("invalidStartTime")) : setEndError(t("invalidEndTime"));
    }
  };

  const handleNextAction = (value, type) => {
    const isMatched = type === "matched";
    const saveFn = isMatched ? setOnMatched : setOnUnMatched;

    // Reset everything
    if (isMatched) {
      setSelectedMatchedQueue({ displayName: "", id: "" });
      setSelectedMatchedUser({ displayName: "", id: "" });
      setSelectedMatchedRouting("");
      setMatchedPhone("");
      const actionType = (value === "transferUser" || value === "transferPhone") ? "transfer" : value;
      setMatchedAction(actionType);
    } else {
      setSelectedUnmatchedQueue({ displayName: "", id: "" });
      setSelectedUnmatchedUser({ displayName: "", id: "" });
      setSelectedUnmatchedRouting("");
      setUnmatchedPhone("");
      const actionType = (value === "transferUser" || value === "transferPhone") ? "transfer" : value;
      setUnmatchedAction(actionType);
    }

    let obj = {};
    if (value === "transferUser") {
      obj = { type: "transfer", targetType: "user" };
    } else if (value === "transferPhone") {
      obj = { type: "transfer", targetType: "phone" };
    } else {
      obj = { type: value };
    }
    
    saveFn(obj);
  };

  return (
    <div>
      {/* Start/End on same line */}
      <div style={{ display: "flex", gap: "2rem", alignItems: "center", marginTop: "1rem" }}>
        <div className={classes.timeRow}>
          <label className={classes.label}>{t("startTime")}</label>
          <input
            type="time"
            className={classes.input}
            value={data?.Time?.start || ""}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onChange={(e) => handleTimeChange(e.target.value, "start")}
          />
          {startError && <Text style={{ color: "red" }}>{startError}</Text>}
        </div>
        
        <div className={classes.timeRow}>
          <label className={classes.label}>{t("endTime")}</label>
          <input
            type="time"
            className={classes.input}
            value={data?.Time?.end || ""}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onChange={(e) => handleTimeChange(e.target.value, "end")}
          />
          {endError && <Text style={{ color: "red" }}>{endError}</Text>}
        </div>
      </div>

      {/* Matched */}
      <div className={classes.section}>
        <Label>{t("ifMatched")}</Label>
        <Dropdown
          className="time-dropdown"
          value={getTranslatedValue(data.onMatched?.type, data.onMatched?.targetType)}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onOptionSelect={(_, dropData) => handleNextAction(dropData.optionValue, "matched")}
        >
          {REGEX_NEXT_ACTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value} text={t(opt.textKey)}>
              {t(opt.textKey)}
            </Option>
          ))}
        </Dropdown>

        {matchedAction === "queue" && (
          <QueueSelectorComponent
            displayName={selectedMatchedQueue.displayName}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            getData={(queue) => {
              setSelectedMatchedQueue(queue);
              setOnMatched({
                type: "queue",
                queueDisplayName: queue.displayName,
                queueId: queue.id,
              });
            }}
          />
        )}

        {(matchedAction === "transferUser" || (matchedAction === "transfer" && data.onMatched?.targetType === "user")) && (
          <SearchResourceInGraph
            displayName={selectedMatchedUser.displayName}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onSelectUser={(user) => {
              setSelectedMatchedUser(user);
              setOnMatched({
                type: "transfer",
                userDisplayName: user.displayName,
                targetId: user.id,
                targetType: "user"
              });
            }}
          />
        )}

        {matchedAction === "routing" && (
          <Dropdown
            className="time-dropdown"
            placeholder={t("selectRouting")}
            value={selectedMatchedRouting}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onOptionSelect={(_, option) => {
              setSelectedMatchedRouting(option.optionValue);
              setOnMatched({ type: "routing", routingName: option.optionValue });
            }}
          >
            {Object.keys(routingList || {})
              .filter((r) => r !== routingName)
              .map((r) => (
                <Option key={r} value={r}>
                  {routingList[r]?.displayName || r}
                </Option>
              ))}
          </Dropdown>
        )}

        {(matchedAction === "transferPhone" || (matchedAction === "transfer" && data.onMatched?.targetType === "phone")) && (
          <input
            type="tel"
            placeholder={t("enterPhoneNumber")}
            className={classes.input}
            value={matchedPhone}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onChange={(e) => {
              setMatchedPhone(e.target.value);
              setOnMatched({ type: "transfer", targetId: e.target.value, targetType: "phone" });
            }}
          />
        )}
      </div>

      {/* Unmatched */}
      <div className={classes.section}>
        <Label>{t("ifNotMatched")}</Label>
        <Dropdown
          className="time-dropdown"
          value={getTranslatedValue(data.onUnMatched?.type, data.onUnMatched?.targetType)}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onOptionSelect={(_, dropData) => handleNextAction(dropData.optionValue, "unmatched")}
        >
          {REGEX_NEXT_ACTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value} text={t(opt.textKey)}>
              {t(opt.textKey)}
            </Option>
          ))}
        </Dropdown>

        {unmatchedAction === "queue" && (
          <QueueSelectorComponent
            displayName={selectedUnmatchedQueue.displayName}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            getData={(queue) => {
              setSelectedUnmatchedQueue(queue);
              setOnUnMatched({
                type: "queue",
                queueDisplayName: queue.displayName,
                queueId: queue.id,
              });
            }}
          />
        )}

        {(unmatchedAction === "transferUser" || (unmatchedAction === "transfer" && data.onUnMatched?.targetType === "user")) && (
          <SearchResourceInGraph
            displayName={selectedUnmatchedUser.displayName}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onSelectUser={(user) => {
              setSelectedUnmatchedUser(user);
              setOnUnMatched({
                type: "transfer",
                userDisplayName: user.displayName,
                targetId: user.id,
                targetType: "user"
              });
            }}
          />
        )}

        {unmatchedAction === "routing" && (
          <Dropdown
            className="time-dropdown"
            placeholder={t("selectRouting")}
            value={selectedUnmatchedRouting}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onOptionSelect={(_, option) => {
              setSelectedUnmatchedRouting(option.optionValue);
              setOnUnMatched({ type: "routing", routingName: option.optionValue });
            }}
          >
            {Object.keys(routingList || {})
              .filter((r) => r !== routingName)
              .map((r) => (
                <Option key={r} value={r}>
                  {routingList[r]?.displayName || r}
                </Option>
              ))}
          </Dropdown>
        )}

        {(unmatchedAction === "transferPhone" || (unmatchedAction === "transfer" && data.onUnMatched?.targetType === "phone")) && (
          <input
            type="tel"
            placeholder={t("enterPhoneNumber")}
            className={classes.input}
            value={unmatchedPhone}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onChange={(e) => {
              setUnmatchedPhone(e.target.value);
              setOnUnMatched({ type: "transfer", targetId: e.target.value, targetType: "phone" });
            }}
          />
        )}
      </div>
    </div>
  );
}
